import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from 'app/core/http/services/auth.service';
import { CognitoService } from '../services/cognito.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false; 

  private readonly IDLE_MS = 15 * 60 * 1000;
  private readonly LAST_REQUEST_AT_KEY = 'lastRequestAt';

  private readonly REFRESH_HINT = '/auth/refresh';

  constructor(private auth: AuthService, private cognitoService: CognitoService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.shouldCountForIdle(request) && this.isIdleExpired()) {
      this.logoutByInactivity();
      return throwError(() => new Error('Sessão encerrada por inatividade.'));
    }

    if (this.shouldCountForIdle(request)) {
      localStorage.setItem(this.LAST_REQUEST_AT_KEY, String(Date.now()));
    }

    const token = localStorage.getItem('userTokenId');
    if (token) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          if (this.shouldCountForIdle(request) && this.isIdleExpired()) {
            this.logoutByInactivity();
            return throwError(() => err);
          }

          return this.handle401Error(request, next);
        }

        return throwError(() => err);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if ((request.url || '').includes(this.REFRESH_HINT)) {
      this.logoutByInactivity();
      return throwError(() => new Error('Refresh falhou.'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;

      return from(this.cognitoService.refreshToken()).pipe(
        switchMap(() => {
          this.isRefreshing = false;
          return next.handle(this.addTokenToRequest(request));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.logoutByInactivity();
          return throwError(() => error);
        })
      );
    } else {
      return next.handle(request);
    }
  }

  private addTokenToRequest(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.auth.getAccessToken(); // Obtém o token do localStorage
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }
    return request;
  }


  private shouldCountForIdle(request: HttpRequest<any>): boolean {
    const url = request.url || '';
    if (url.includes(this.REFRESH_HINT)) return false; 
    return true;
  }

  private isIdleExpired(): boolean {
    const raw = localStorage.getItem(this.LAST_REQUEST_AT_KEY);
    if (!raw) return false;

    const last = Number(raw);
    if (!Number.isFinite(last)) return false;

    return Date.now() - last >= this.IDLE_MS;
  }

  private logoutByInactivity(): void {

    this.auth.logoutByInactivate?.();
  }
}
